import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { saveApiData } from '../store/Slice/Global/GlobalSlice';
import { loginApi } from '../utils/Apis';
import { isValidValue } from '../utils/CommonFunction';

function UpdatePassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginMailId = sessionStorage.getItem('loginMail');

  // Form state
  const [formData, setFormData] = useState({
    username: loginMailId || '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!isValidValue(formData.username)) {
      newErrors.username = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.username)) {
        newErrors.username = 'Please enter a valid email address';
      }
    }

    // Old password validation
    if (!isValidValue(formData.oldPassword)) {
      newErrors.oldPassword = 'Current password is required';
    } else if (formData.oldPassword.length < 6) {
      newErrors.oldPassword = 'Password must be at least 6 characters';
    }

    // New password validation
    if (!isValidValue(formData.newPassword)) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }

    // Confirm password validation
    if (!isValidValue(formData.confirmPassword)) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as old password
    if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const finalData = {
        username: formData.username.trim(),
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      };

      // Dispatch API call
      const res = await dispatch(saveApiData(loginApi?.updatePassword, finalData));

      if (res?.status === 'success' || res?.payload?.status === 'success') {
        setSuccessMessage('Password updated successfully. Redirecting to login...');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      } else {
        setErrorMessage(res?.message || res?.payload?.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Update password error:', error);
      setErrorMessage(error?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Clear messages
  const clearMessage = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        overflow: 'hidden',
        bgcolor: '#ffffff',
        position: 'relative'
      }}
    >
      <Helmet>
        <title>Update Password | Real Value Partner Portal</title>
      </Helmet>

      {/* ── Left Column: Form Panel ── */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%', lg: '37%', xl: '34%' },
          minWidth: { md: '400px', lg: '430px' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 5, md: 5, lg: 6 },
          py: 3,
          boxSizing: 'border-box',
          bgcolor: '#ffffff',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '380px' }}>
          {/* Logo */}
          <Box sx={{ mb: 2.5 }}>
            <img
              src="/static/logos/realValueLogo.jpg"
              alt="REALVALUE FINLOAN SERVICES PVT LTD"
              style={{ height: '44px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>

          {/* Subheader & Welcome Title */}
          <Typography
            sx={{
              fontSize: '11.5px',
              fontWeight: 700,
              color: '#8e9cb2',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              mb: 0.4
            }}
          >
            DSA PARTNER PORTAL
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#0b132a',
              fontSize: { xs: '26px', sm: '30px' },
              mb: 0.4,
              letterSpacing: '-0.5px'
            }}
          >
            Update Password
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#8e9cb2',
              fontSize: '13.5px',
              mb: 3
            }}
          >
            Secure your account with a new password
          </Typography>

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" onClose={clearMessage} sx={{ mb: 2, borderRadius: '10px' }}>
              {successMessage}
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert
              severity="error"
              action={
                <IconButton size="small" color="inherit" onClick={clearMessage}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{ mb: 2, borderRadius: '10px' }}
            >
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={handleUpdatePassword}>
            {/* Email Input */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Email Address
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                name="username"
                type="email"
                placeholder="Email Address"
                value={formData.username}
                onChange={handleInputChange}
                disabled
                startAdornment={
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0284c7' }
                }}
              />
              {errors.username && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                  {errors.username}
                </Typography>
              )}
            </Box>

            {/* Old Password Input */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Current Password
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                placeholder="Enter current password"
                value={formData.oldPassword}
                onChange={handleInputChange}
                startAdornment={
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#64748b', p: 0.5 }}
                    >
                      {showOldPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 19 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 19 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0284c7' }
                }}
              />
              {errors.oldPassword && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                  {errors.oldPassword}
                </Typography>
              )}
            </Box>

            {/* New Password Input */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                New Password
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleInputChange}
                startAdornment={
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#64748b', p: 0.5 }}
                    >
                      {showNewPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 19 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 19 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0284c7' }
                }}
              />
              {errors.newPassword && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                  {errors.newPassword}
                </Typography>
              )}
            </Box>

            {/* Confirm Password Input */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Confirm New Password
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                startAdornment={
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#64748b', p: 0.5 }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 19 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 19 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0284c7' }
                }}
              />
              {errors.confirmPassword && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                  {errors.confirmPassword}
                </Typography>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                height: '48px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0369a1',
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
            </Button>
          </form>
        </Box>
      </Box>

      {/* ── Right Column: Hero Banner Section ── */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: { md: 6, lg: 8, xl: 9 },
          px: { md: 5, lg: 7, xl: 9 },
          backgroundImage: 'url(/static/login/login_bg_new.png)',
          backgroundSize: 'cover',
          backgroundPosition: '85% center',
          backgroundRepeat: 'no-repeat',
          imageRendering: '-webkit-optimize-contrast',
          filter: 'contrast(1.03) brightness(1.01) saturate(1.04)',
          position: 'relative'
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Subtitle Header */}
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '1px',
              color: '#0284c7',
              textTransform: 'uppercase',
              mb: 1.5
            }}
          >
            LOAN MANAGEMENT SYSTEM
          </Typography>

          {/* Main Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { md: '36px', lg: '42px' },
              fontWeight: 900,
              color: '#0b132a',
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
              mb: 1.5
            }}
          >
            All Your Loan Business <br />
            <span style={{ color: '#0284c7' }}>in One Place</span>
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: '14.5px',
              color: '#475569',
              lineHeight: 1.55,
              mb: 4,
              maxWidth: '620px'
            }}
          >
            Onboard partners, manage customers, track applications, resolve document issues,
            calculate payouts and grow your business with REALVALUE.
          </Typography>

          {/* 4 Feature Cards in a single horizontal row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 1.5,
              maxWidth: '950px'
            }}
          >
            {/* Card 1 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0284c7',
                  flexShrink: 0
                }}
              >
                <GroupAddOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Partner Onboarding
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Manage your network
                </Typography>
              </Box>
            </Box>

            {/* Card 2 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#16a34a',
                  flexShrink: 0
                }}
              >
                <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Customer Management
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Track applications
                </Typography>
              </Box>
            </Box>

            {/* Card 3 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9333ea',
                  flexShrink: 0
                }}
              >
                <InsertChartOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Payout & Reports
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Real-time insights
                </Typography>
              </Box>
            </Box>

            {/* Card 4 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#ffe4e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#e11d48',
                  flexShrink: 0
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Secure & Reliable
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Enterprise grade
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Bottom Center Blue Footer ── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#0284c7',
          color: '#ffffff',
          py: 0.8,
          textAlign: 'center',
          zIndex: 10,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.08)'
        }}
      >
        <Typography
          sx={{
            fontSize: '12.5px',
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '0.4px'
          }}
        >
          Powered by Kalolytic
        </Typography>
      </Box>
    </Box>
  );
}

export default UpdatePassword;
